/* Libraries */
import React, { FC, useMemo, useCallback } from 'react';
import { useObserver } from 'mobx-react';

/* Hooks */
import useMst from '~/src/hooks/useMst';
import usePdfCourtFormsActions from '~/src/hooks/usePdfCourtFormsActions';

/* Components */
import PdfTableWrapper, {
  PdfCourtForm,
} from '~/src/components/CourtForms/PdfTableWrapper';
import { stateCodeToStateName } from '~/src/utils/stateCodeToStateName';
import { TOTAL_DOCUMENT_LIMIT } from '../utils/constants';
import { GENERAL_FEDERAL_FORMS_CATEGORY } from './FederalFormsWithLocalitiesContainer';
import type { BreadcrumbDataItem } from '~/src/components/Breadcrumb/Breadcrumb';
import useFeatureAccesses from '~/src/hooks/useFeatureAccesses';

/* Types */

interface Props {
  filterCategory: string;
  locality?: string;
  practiceArea?: string;
  region?: string;
  routeUrl?: string;
  onPreviewTemplate?: (template: PdfCourtForm) => void;
  disableBreadcrumb?: boolean;
}

const PdfTableContainer: FC<Props> = ({
  filterCategory,
  practiceArea,
  locality,
  region,
  routeUrl = '/library/court-forms',
  onPreviewTemplate,
  disableBreadcrumb = false,
}) => {
  const { sidebarItemTotalNumber, containedInSidebar } = useMst((store) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useObserver(() => ({
      sidebarItemTotalNumber: store.sidebarItems.items.length,
      containedInSidebar: store.sidebarItems.containedInSidebar,
    })),
  );

  const {
    addTemplateToSeletionSidebar: handleAddTemplateToSeletionSidebar,
    previewTemplate: handlePreviewTemplate,
    createFormTemplate: handleCreateFromTemplate,
  } = usePdfCourtFormsActions();

  // Memoize the functions to prevent unnecessary re-renders
  const memoizedHandleAddTemplateToSeletionSidebar = useCallback(
    handleAddTemplateToSeletionSidebar,
    [handleAddTemplateToSeletionSidebar],
  );
  const memoizedHandlePreviewTemplate = useCallback(handlePreviewTemplate, [
    handlePreviewTemplate,
  ]);
  const memoizedHandleCreateFromTemplate = useCallback(
    handleCreateFromTemplate,
    [handleCreateFromTemplate],
  );

  // Memoize the preview handler to prevent inline function recreation
  const stableHandlePreviewTemplate = useCallback(
    (template: PdfCourtForm) =>
      onPreviewTemplate?.(template) ?? memoizedHandlePreviewTemplate(template),
    [onPreviewTemplate, memoizedHandlePreviewTemplate],
  );

  const { isFederalFormsCategoriesEnabled } = useFeatureAccesses();
  const federalFormsLabel = isFederalFormsCategoriesEnabled
    ? 'Federal forms'
    : 'USCIS forms';

  const pdfRowActions = useMemo(() => {
    return {
      previewFormTemplate: memoizedHandlePreviewTemplate,
      createFormTemplate: memoizedHandleCreateFromTemplate,
    };
  }, [memoizedHandlePreviewTemplate, memoizedHandleCreateFromTemplate]);

  const breadcrumbData = useMemo(() => {
    const data: BreadcrumbDataItem[] = [
      {
        label: 'Court forms',
        url: routeUrl,
      },
    ];

    if (filterCategory === 'FEDERAL') {
      data.push({
        label: federalFormsLabel,
        url: `${routeUrl}/federal_USCIS`,
      });
      if (isFederalFormsCategoriesEnabled && locality) {
        data.push({
          label:
            locality === 'None' ? GENERAL_FEDERAL_FORMS_CATEGORY : locality,
        });
      }
    } else {
      // filterCategory is stateCode
      let stateName;
      if (filterCategory in stateCodeToStateName) {
        stateName =
          stateCodeToStateName[
            filterCategory as keyof typeof stateCodeToStateName
          ];
      } else {
        stateName = filterCategory;
      }
      if (locality && locality !== 'None') {
        // from region list
        data.push({
          label: `${stateName} regional forms`,
          url: `${routeUrl}/${filterCategory}/locality-listing`,
        });
        data.push({
          label: locality,
        });
      } else {
        data.push({
          label: `${stateName} forms`,
        });
      }
    }
    return data;
  }, [
    filterCategory,
    locality,
    routeUrl,
    federalFormsLabel,
    isFederalFormsCategoriesEnabled,
  ]);

  return (
    <PdfTableWrapper
      stateCode={filterCategory}
      locality={locality}
      practiceArea={practiceArea}
      region={region}
      disableAddButton={sidebarItemTotalNumber >= TOTAL_DOCUMENT_LIMIT}
      breadcrumbData={breadcrumbData}
      rowActions={onPreviewTemplate ? undefined : pdfRowActions}
      containedInSidebar={containedInSidebar}
      handleAddTemplateToSeletionSidebar={
        memoizedHandleAddTemplateToSeletionSidebar
      }
      handlePreviewTemplate={stableHandlePreviewTemplate}
      disableBreadcrumb={disableBreadcrumb}
    />
  );
};

export default PdfTableContainer;
